import React from "react";
import LegalPage, { Mail, Ext, In, P, List, DataCard } from "./LegalPage";

// What this page says is what the backend does. When a retention period or a
// stored field changes in backend-/, change it here in the same release.

const sections = [
  {
    id: "who",
    title: "Who is responsible",
    body: (
      <>
        <P>
          Fennira Cloud is the Fennira Studio at studio.fennira.com, the Fennira cloud streaming relay, and the account
          features of the Fennira Broadcast Android app. Fennira is an independent project and the controller of the personal data described here (“we”, “us”).
        </P>
        <P>For anything about your data, write to <Mail />.</P>
      </>
    ),
  },
  {
    id: "short",
    title: "The short version",
    body: (
      <List items={[
        "An account is a passkey and a name you choose for it (yours, your studio's or your show's). We don't ask for an email address or a phone number.",
        "Your stream goes through our servers to the platforms you choose. We don't record it.",
        "Stream keys and platform access tokens are stored encrypted.",
        "IP addresses tied to your account are deleted after 7 days.",
        "No advertising, no analytics, no tracking cookies, and we never sell your data.",
        "You can delete your account and everything in it yourself, at any time.",
      ]} />
    ),
  },
  {
    id: "collect",
    title: "What we collect and why",
    body: (
      <>
        <P>
          Each item below says what we keep, why, and for how long. Unless stated otherwise, we process it because it is
          necessary to provide the service you asked for (performance of a contract, GDPR art. 6(1)(b)). Security data
          is processed for our legitimate interest in keeping accounts and the service safe (art. 6(1)(f)).
        </P>
        <DataCard
          title="Your passkeys"
          what="For each passkey: its public key and identifier, the name you gave it, an identifier of the kind of authenticator that made it, and when it was added and last used. Your account also has a random internal identifier. The private part of a passkey never leaves your device or your passkey provider; we never see it, nor your fingerprint, face or screen lock."
          why="To sign you in."
          kept="Until you remove the passkey or delete your account."
        />
        <DataCard
          title="Your recovery code"
          what="Only a keyed hash of it. We cannot read the code itself or show it to you again."
          why="To let you back in if you lose all your passkeys."
          kept="Until you create a new code or delete your account. A code works once."
        />
        <DataCard
          title="Email address for alerts (optional)"
          what="The address you add, and whether you confirmed it."
          why="To tell you about security events (recovery code used, passkey added, account deleted). It is never used to sign in, to recover your account, or for marketing. Basis: your consent; you can remove it at any time in Settings → Security."
          kept="Until you remove it or delete your account."
        />
        <DataCard
          title="Account name"
          what="The name you give your account when you create it, or later in Settings → Security. It can be your name, your studio's or your show's."
          why="To show it in the Studio and to label your passkeys in your password manager, so your accounts are easy to tell apart."
          kept="Until you change it or delete your account."
        />
        <DataCard
          title="Older accounts"
          what="Accounts created before passkeys may also hold a first and last name, an email address, a phone number, a password hash and, if you used it, your Google account identifier and the name and email that Google shared."
          why="To keep those accounts working."
          kept="Until you delete your account."
        />
        <DataCard
          title="Devices and sessions"
          what="For each signed-in device: a name built from your browser or phone model (for example “Chrome on Windows”), whether it is the web, the app or the app's Studio view, how you signed in, and when it was created, last used and signed out. IP addresses used by each session are stored separately."
          why="To show you your devices, let you sign any of them out, and detect a stolen session."
          kept="IP addresses: 7 days. Sessions end after 30 days without use or 90 days in total, and their records are deleted 30 days after they end."
        />
        <DataCard
          title="Signing in a computer with a code from your phone"
          what="When a computer asks to be signed in (or confirmed) with a code, we keep, for at most 2 minutes, the code, the computer's browser name and its IP address. Your phone is shown the browser name and an approximate place (city and country) worked out from that IP address. The place is looked up on our own server in the DB-IP Lite database (IP geolocation by DB-IP, db-ip.com, CC BY 4.0); no IP address is sent to anyone else. If you approve, the computer becomes one of your devices (see “Devices and sessions”)."
          why="So you can see what you are approving before you approve it, and notice a request from somewhere you are not. Basis: legitimate interest in account security."
          kept="The request is deleted when it is approved, refused or after 2 minutes, and is never written to disk. A computer you approve is kept as described under “Devices and sessions”."
        />
        <DataCard
          title="Security alerts"
          what="Events such as “recovery code used” or “passkey added”, with the device name and time, and whether you confirmed them."
          why="To show them to you and let you react (“This wasn't me”). Basis: legitimate interest in account security."
          kept="Until you delete your account."
        />
        <DataCard
          title="Your streaming setup"
          what="The stream keys and RTMP server addresses you save, their labels, and the one-hour key your encoder uses to send to Fennira. Keys are encrypted (AES-256-GCM) and only ever shown to you masked, or after a fresh passkey check."
          why="To send your stream where you want it."
          kept="Until you remove them or delete your account. The encoder key expires after one hour."
        />
        <DataCard
          title="Connected YouTube and Facebook accounts"
          what="Access tokens (encrypted), the channel or Page you chose and its name, the title and description you set for your broadcasts, and which permissions were granted. See section 6 for Google and Meta."
          why="To create the live broadcasts on your channel or Page and send your stream to them."
          kept="Until you disconnect the account in the Studio or delete your Fennira account; both delete it immediately."
        />
        <DataCard
          title="Your live streams"
          what="The video and audio you send, and live statistics such as bit rate and frame rate."
          why="To copy your stream, in real time, to your destinations and to show you its status."
          kept="Not stored. The stream passes through memory and is not recorded. Statistics exist only while you are live."
        />
        <DataCard
          title="Videos you upload"
          what="Video files you upload to stream from a file, in your own storage space (10 GB unless the Studio shows otherwise)."
          why="To stream them when you ask."
          kept="Until you delete them or your account."
        />
        <DataCard
          title="Server logs"
          what="Technical records of requests and errors: time, IP address, address requested, result. They are written so as not to contain passwords, keys or tokens."
          why="To run the service, fix faults and investigate abuse. Basis: legitimate interest."
          kept="Deleted automatically within 30 days."
        />
        <DataCard
          title="Anti-abuse checks"
          what="When you create an account, your device solves a small computing puzzle (ALTCHA). It runs on our server, not a third party's, and records nothing about you. Request rates per IP address are counted in memory."
          why="To stop automated sign-ups and password or code guessing. Basis: legitimate interest."
          kept="The counters reset within an hour and are never written to disk."
        />
      </>
    ),
  },
  {
    id: "device",
    title: "Cookies and storage on your device",
    body: (
      <>
        <P>
          The Studio stores your sign-in tokens and a few display preferences (for example which source you used last)
          in your browser's local storage. They are needed for the Studio to work, so they need no consent. Signing out
          removes the sign-in tokens.
        </P>
        <P>
          We use no advertising or analytics cookies, no tracking pixels and no third-party scripts. The Studio's fonts
          are served from our own server.
        </P>
      </>
    ),
  },
  {
    id: "sharing",
    title: "Who receives your data",
    body: (
      <>
        <P>We never sell your data or share it for advertising. It goes only to:</P>
        <List items={[
          <><b>The platforms you choose.</b> When you go live, we send your stream, and for YouTube and Facebook the
            title and description you set, to the destinations you configured: YouTube (Google), Facebook (Meta),
            TikTok, LinkedIn, X, Kick or any RTMP server you add. What they do with it is governed by their own terms
            and privacy policies.</>,
          <><b>Our hosting provider</b>, which runs our servers in the European Union and sends our
            emails. It acts as our processor under a data-processing agreement and may not use the data for anything
            else.</>,
          <><b>Google</b>, if you sign in to an older account with Google: Google learns that you signed in to Fennira.</>,
          <><b>Authorities</b>, when the law requires it. We check that every request has a legal basis and disclose no
            more than required.</>,
        ]} />
        <P>
          Your passkeys themselves are stored by your passkey provider (for example Google Password Manager, iCloud
          Keychain or a security key) under that provider's terms; we never receive them.
        </P>
      </>
    ),
  },
  {
    id: "youtube",
    title: "Google, YouTube and Meta",
    body: (
      <>
        <P>
          Fennira uses the YouTube API Services when you connect a YouTube channel. By connecting one, you agree to
          the <Ext href="https://www.youtube.com/t/terms">YouTube Terms of Service</Ext>, and the{" "}
          <Ext href="https://policies.google.com/privacy">Google Privacy Policy</Ext> applies to Google's handling of
          your data.
        </P>
        <P>
          With the access you grant, Fennira reads your channel's identity and name, and creates, updates and ends
          live broadcasts on it when you go live. We don't read your other videos, comments, subscribers or analytics,
          and we don't share this data with anyone. Fennira's use and transfer of information received from Google
          APIs adheres to the{" "}
          <Ext href="https://developers.google.com/terms/api-services-user-data-policy">Google API Services User Data Policy</Ext>,
          including the Limited Use requirements.
        </P>
        <P>
          For Facebook, Fennira reads the list of Pages you manage and creates live videos on the Page you choose. The{" "}
          <Ext href="https://www.facebook.com/privacy/policy">Meta Privacy Policy</Ext> applies to Meta's handling of your data.
        </P>
        <P>You can take this access back at any time:</P>
        <List items={[
          <>in the Studio: choose Disconnect on the YouTube or Facebook account. We delete the stored tokens and channel or Page details at once;</>,
          <>at Google: <Ext href="https://myaccount.google.com/permissions">Google Account → Third-party connections</Ext>;</>,
          <>at Meta: Facebook Settings → Business integrations.</>,
        ]} />
        <P>
          <b>Deleting your data.</b> Deleting your Fennira account (section 9) deletes everything we hold from Google and
          Meta. You can also ask us at <Mail />.
        </P>
      </>
    ),
  },
  {
    id: "app",
    title: "The Fennira Broadcast app",
    body: (
      <>
        <P>
          The app uses your camera and microphone to make the stream, on your phone. Streaming straight to a platform
          (without Fennira Cloud) doesn't involve our servers or an account. When you sign in, the app uses the same
          account, passkeys and data described above, and in cloud mode it sends your stream to Fennira, which relays
          it as described in section 3.
        </P>
        <P>The app contains no advertising or analytics, and is built so that keys, codes and tokens are not written to the device logs.</P>
      </>
    ),
  },
  {
    id: "security",
    title: "Security and where your data is",
    body: (
      <>
        <List items={[
          "Passkeys can't be phished or reused on another site, and we store no password for them.",
          "Stream keys, platform tokens and the encoder key are encrypted at rest; the recovery code is stored only as a keyed hash.",
          "The Studio and the API are only reachable over HTTPS.",
          "Sensitive actions (showing a key, connecting a platform, deleting the account) ask for your passkey again if your last check is more than 5 minutes old.",
          "After a recovery-code sign-in, keys and destinations stay locked for 24 hours and you are alerted, in case it wasn't you.",
        ]} />
        <P>
          <b>One limit to know about:</b> the connection from your encoder to Fennira uses RTMP, which is not encrypted
          in transit. That's why the key it uses expires after one hour.
        </P>
        <P>
          Our servers are in the European Union. When you send a stream or data to a
          platform outside the European Economic Area, such as YouTube or Facebook, that platform's transfer
          safeguards apply.
        </P>
      </>
    ),
  },
  {
    id: "rights",
    title: "Your rights",
    body: (
      <>
        <P>
          Under the GDPR and similar laws, you can ask to access, correct, delete or receive a copy of your data, to
          restrict or object to its use, and to withdraw any consent you gave. Most of it you can do yourself:
        </P>
        <List items={[
          <><b>Delete your account:</b> <In to="/settings/security">Settings → Security</In> → Delete account. This
            immediately deletes your account, passkeys, IP records, alerts, stream keys, platform connections and
            uploaded videos, and signs out every device. The records of those signed-out sessions (device name, dates)
            are deleted 30 days later, and server logs within 30 days. If you added an alert email, we send one last
            receipt to it.</>,
          <><b>Devices, passkeys, alert email:</b> view and remove them in Settings → Security.</>,
          <><b>Stream keys and destinations:</b> view, change and remove them in the Studio.</>,
        ]} />
        <P>
          For anything else, write to <Mail />. Because a name alone doesn't prove an account is yours, we may ask you to send the
          request from a signed-in session, or to sign in with your passkey, to prove the account is yours. We answer
          within one month.
        </P>
        <P>
          If you think we haven't respected your rights, you can also complain to a data-protection authority, in
          particular the one in the country where you live.
        </P>
      </>
    ),
  },
  {
    id: "age",
    title: "Age",
    body: <P>Fennira is for people aged 16 or over. We don't knowingly hold data about anyone younger; if you believe we do, write to <Mail /> and we will delete it.</P>,
  },
  {
    id: "changes",
    title: "Changes to this policy",
    body: (
      <P>
        If we change this policy, we update the date at the top. If a change matters, for example new data or a new
        recipient, we tell you in the Studio and, if you added one, by alert email before it applies.
      </P>
    ),
  },
];

export default function Privacy() {
  return (
    <LegalPage
      eyebrow="Legal"
      title="Privacy Policy"
      intro={<P>This policy explains what personal data Fennira Cloud handles, why, for how long, and what you can do about it.</P>}
      sections={sections}
    />
  );
}
